import React, { useState } from "react";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import EconomicImg from "../assets/rooms/economic.webp";
import DoubleBedImg from "../assets/rooms/double.webp";
import FamilyImg from "../assets/rooms/family.webp";
import DeluxeImg from "../assets/rooms/Delux.webp";
import HutImg from "../assets/rooms/hut.webp";
import CottageImg from "../assets/rooms/cottage.webp";

const roomPreviews = {
  "Economic": EconomicImg,
  "Double Bed": DoubleBedImg,
  "Family": FamilyImg,
  "Deluxe": DeluxeImg,
  "Hut": HutImg,
  "Cottage": CottageImg
};

function Room() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    room: "",
    guests: "",
    checkin: "",
    checkout: "",
    message: "",
  });

  const today = new Date().toISOString().split("T")[0];
  const [sent, setSent] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setForm((prev) => {
      const updated = { ...prev, [name]: value };
      
      // Reset checkout date if it conflicts with a newly changed checkin date
      if (name === "checkin" && prev.checkout && value > prev.checkout) {
        updated.checkout = "";
      }
      return updated;
    });
  };

  // 🌍 GLOBAL PHONE VALIDATION
  const isValidPhone = (phone) => {
    if (!phone) return false;
    const phoneNumber = parsePhoneNumberFromString(phone);
    return phoneNumber ? phoneNumber.isValid() : false;
  };

  const isValid =
    form.name.trim() &&
    form.room &&
    form.guests &&
    form.checkin &&
    form.checkout &&
    isValidPhone(form.phone);

  const handleWhatsApp = () => {
    if (!isValid) return;

    const text = `🏥 Room Booking Request

👤 Name: ${form.name.trim()}
📞 Phone: ${form.phone}
🏠 Room Type: ${form.room}
👥 Guests: ${form.guests}
📅 Check-in: ${form.checkin}
📅 Check-out: ${form.checkout}
📝 Message: ${form.message.trim() || "N/A"}`;

    const clientPhone = "919443043074";
    const url = `https://api.whatsapp.com/send?phone=${clientPhone}&text=${encodeURIComponent(text)}`;

    window.open(url, "_blank");

    // Show success message banner
    setSent(true);

    // RESET FORM STATE
    setForm({
      name: "",
      phone: "",
      room: "",
      guests: "",
      checkin: "",
      checkout: "",
      message: "",
    });

    setTimeout(() => setSent(false), 5000);
  };

  return (
    <section className="bg-gradient-to-b from-green-50 to-white px-4 py-16 md:px-16 min-h-screen font-poppins flex justify-center items-center text-neutral-900">
      
      <div className="w-full max-w-xl bg-white p-6 md:p-10 rounded-3xl shadow-2xl border border-green-100">
        
        <div className="text-center mb-8">
          <h2 className="text-3xl font-black text-green-900 tracking-wide">
            Room Booking Request
          </h2>
          <p className="mt-2 text-green-700 font-bold text-sm">
            Select your preferred room type and schedule below
          </p>
        </div>

        {/* SUCCESS MESSAGE BANNER */}
        {sent && (
          <div className="mb-6 p-4 bg-green-100 border border-green-200 text-green-900 rounded-2xl text-center font-bold shadow-sm animate-fade-in">
            🙏 Thank you for your booking request! Your details have been generated for WhatsApp. Our team will assist you shortly.
          </div>
        )}

        <div className="space-y-5">
          
          {/* NAME */}
          <div>
            <label className="block text-green-900 font-black text-sm mb-2">👤 Full Name</label>
            <input
              name="name"
              type="text"
              value={form.name}
              onChange={handleChange}
              className="w-full p-3 bg-green-50/20 border border-green-100 rounded-xl focus:ring-2 focus:ring-green-600 focus:border-green-600 focus:bg-white outline-none transition font-semibold"
              placeholder="Enter your name"
            />
          </div>

          {/* PHONE */}
          <div>
            <label className="block text-green-900 font-black text-sm mb-2">📞 Contact Number</label>
            <input
              name="phone"
              type="tel"
              value={form.phone}
              onChange={handleChange}
              className={`w-full p-3 bg-green-50/20 border rounded-xl focus:ring-2 focus:bg-white outline-none transition font-semibold ${
                form.phone && !isValidPhone(form.phone) 
                  ? "border-red-300 focus:ring-red-500 focus:border-red-500" 
                  : "border-green-100 focus:ring-green-600 focus:border-green-600"
              }`}
              placeholder="Include country code (e.g. +919443043074)"
            />
            {/* PHONE ERROR */}
            {form.phone && !isValidPhone(form.phone) && (
              <p className="text-red-600 text-xs font-bold mt-1.5 pl-1">
                ⚠️ Please enter a valid international number with standard '+' prefix.
              </p>
            )}
          </div>

          {/* ROOM TYPE */}
          <div>
            <label className="block text-green-900 font-black text-sm mb-2">🏠 Accomodation Selection</label>
            <select
              name="room"
              value={form.room}
              onChange={handleChange}
              className="w-full p-3 bg-green-50/20 border border-green-100 rounded-xl focus:ring-2 focus:ring-green-600 focus:border-green-600 focus:bg-white outline-none transition font-bold text-neutral-800"
            >
              <option value="" className="font-bold">-- Select Room Type --</option>
              <option value="Dormitory" className="font-bold">Dormitory</option>
              <option value="Economic" className="font-bold">Economic Room</option>
              <option value="Double Bed" className="font-bold">Double Bed Room</option>
              <option value="Family" className="font-bold">Family Room</option>
              <option value="Deluxe" className="font-bold">Deluxe Room</option>
              <option value="Hut" className="font-bold">Hut (Traditional)</option>
              <option value="Cottage" className="font-bold">Cottage Room</option>
            </select>
          </div>
  {/* 🖼️ PICTURES 2-8: DYNAMIC ROOM PREVIEWS */}
          {form.room && roomPreviews[form.room] && (
            <div className="w-full h-48 rounded-2xl overflow-hidden shadow-md border border-green-100 transition-all duration-300 transform scale-100">
              <img 
                src={roomPreviews[form.room]} 
                alt={`${form.room} preview`} 
               // className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* GUESTS */}
          <div>
            <label className="block text-green-900 font-black text-sm mb-2">👥 Total Guests</label>
            <input
              name="guests"
              type="number"
              min="1"
              value={form.guests}
              onChange={handleChange}
              className="w-full p-3 bg-green-50/20 border border-green-100 rounded-xl focus:ring-2 focus:ring-green-600 focus:border-green-600 focus:bg-white outline-none transition font-semibold"
              placeholder="Number of persons staying"
            />
          </div>

          {/* DATES GRID */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* CHECK-IN */}
            <div>
              <label className="block text-green-900 font-black text-sm mb-2">📅 Check-In Date</label>
              <input
                name="checkin"
                type="date"
                min={today}
                value={form.checkin}
                onChange={handleChange}
                className="w-full p-3 bg-green-50/20 border border-green-100 rounded-xl focus:ring-2 focus:ring-green-600 focus:border-green-600 focus:bg-white outline-none transition font-bold text-neutral-800"
              />
            </div>

            {/* CHECK-OUT */}
            <div>
              <label className="block text-green-900 font-black text-sm mb-2">📅 Check-Out Date</label>
              <input
                name="checkout"
                type="date"
                min={form.checkin || today}
                value={form.checkout}
                onChange={handleChange}
                disabled={!form.checkin}
                className={`w-full p-3 border rounded-xl focus:ring-2 focus:bg-white outline-none transition font-bold text-neutral-800 ${
                  form.checkin 
                    ? "bg-green-50/20 border-green-100 focus:ring-green-600 focus:border-green-600 cursor-pointer" 
                    : "bg-gray-100 border-gray-200 cursor-not-allowed text-gray-400"
                }`}
              />
            </div>
          </div>

          {/* MESSAGE */}
          <div>
            <label className="block text-green-900 font-black text-sm mb-2">📝 Special Requests or Health Notes (Optional)</label>
            <textarea
              name="message"
              rows="3"
              value={form.message}
              onChange={handleChange}
              className="w-full p-3 bg-green-50/20 border border-green-100 rounded-xl focus:ring-2 focus:ring-green-600 focus:border-green-600 focus:bg-white outline-none transition font-semibold resize-none"
              placeholder="Let us know about dietary choices, medical history conditions, or specific treatments needed..."
            />
          </div>

          {/* ACTION SUBMIT BUTTON */}
          <div className="pt-2">
            <button
              type="button"
              onClick={handleWhatsApp}
              disabled={!isValid}
              className={`w-full p-4 rounded-xl font-black text-base md:text-lg text-white shadow-md transition-all duration-300 transform active:scale-95 flex items-center justify-center gap-2 ${
                isValid 
                  ? "bg-green-700 hover:bg-green-800 cursor-pointer shadow-green-200" 
                  : "bg-neutral-300 cursor-not-allowed shadow-none"
              }`}
            >
              <span>💬</span> Send Booking Request via WhatsApp
            </button>
          </div>

        </div>
      </div>

    </section>
  );
}

export default Room;